import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import type { PendingAction } from "@/hooks/useCopilot";

interface CopilotActionCardProps {
  action: PendingAction;
  onConfirm: () => void;
  onDecline: () => void;
}

const actionLabels: Record<string, string> = {
  resend_onboarding_email: "Resend Onboarding Email",
  create_internal_note: "Create Internal Note",
  close_support_ticket: "Close Support Ticket",
  acknowledge_incident: "Acknowledge Incident",
  resolve_incident: "Resolve Incident",
};

export function CopilotActionCard({ action, onConfirm, onDecline }: CopilotActionCardProps) {
  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-xs font-semibold">Action Requires Confirmation</span>
        </div>

        <div className="bg-background rounded px-2 py-1.5 text-xs space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Action:</span>
            <span className="font-medium">{actionLabels[action.toolName] ?? action.toolName}</span>
          </div>
          {Object.entries(action.args).map(([key, val]) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}:</span>
              <span className="font-mono text-[10px]">{String(val)}</span>
            </div>
          ))}
        </div>

        <p className="text-[10px] text-muted-foreground">{action.description}</p>

        <div className="flex gap-2">
          <Button
            size="sm"
            className="h-7 text-xs flex-1"
            onClick={onConfirm}
          >
            <CheckCircle className="h-3 w-3 mr-1" />
            Confirm & Execute
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={onDecline}
          >
            <XCircle className="h-3 w-3 mr-1" />
            Decline
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
